import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { Box, IconButton, Table, TableBody, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { useModal } from 'src/hooks';
import { IAssemblyTag, IAssemblyTagWithAssembliesCount } from 'src/interfaces';
import { DataTestID, hasPaginatedData, lang, useUserRole } from 'src/utils';

import { useManageAssemblyTagPage } from '../../hooks/ManageAssemblyTagPage.hook';
import { SORT_DIRECTION } from '../../ManageAssemblyTagPage.interface';
import {
  ArrowIcon,
  CustomTableCell,
  CustomTableCellRight,
  CustomTableHead,
  CustomTableRow,
  InViewBoxContainer,
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
  TableBodyContainer,
} from '../../ManageAssemblyTagPage.styled';

export const ManageAssemblyTagGridTable = () => {
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();
  const [selectedAssemblyTag, setSelectedAssemblyTag] = useState(0);
  const [deletingStatus, setDeletingStatus] = useState<{
    [key: number]: boolean;
  }>({});
  const deleteConfirmDialog = useModal();
  const {
    isLoading,
    getNextAssemblyTagWithAssembliesCountPage,
    columns,
    handleSort,
    isActive,
    activeColumn,
    assemblyTagWithAssembliesCount,
    sorting,
    hasNextPage,
    assemblies,
    deleteAssemblyTag,
    updateAssemblies,
  } = useManageAssemblyTagPage();

  useEffect(() => {
    if (inView) {
      getNextAssemblyTagWithAssembliesCountPage();
    }
  }, [getNextAssemblyTagWithAssembliesCountPage, inView]);

  if (isLoading) {
    return <Progress />;
  }

  const hasData = hasPaginatedData(assemblyTagWithAssembliesCount);

  const assemblyTagWithAssemblyCountData = (
    row: IAssemblyTagWithAssembliesCount,
    column: (typeof columns)[number],
  ) => {
    const columnValues =
      row[column.key as keyof IAssemblyTagWithAssembliesCount];

    if (typeof columnValues === 'string' && columnValues.length > 30) {
      return (
        <CustomTooltip
          width={500}
          placement="bottom"
          title={columnValues}
          overflowWidth={250}>
          <span>{columnValues}</span>
        </CustomTooltip>
      );
    }

    return <span>{columnValues}</span>;
  };

  const deleteTag = async (tag: IAssemblyTag) => {
    try {
      const tagAssemblies = assemblies.filter(
        (assembly: { assembly_tag_id: number[] }) => {
          return assembly.assembly_tag_id?.includes(tag.id);
        },
      );
      const updatedAssemblies = tagAssemblies.map(
        (assembly: { assembly_tag_id: any[] }) => ({
          ...assembly,
          assembly_tag_id: assembly.assembly_tag_id.filter(
            (assemblyTagId: number) => assemblyTagId !== tag.id,
          ),
        }),
      );
      setDeletingStatus((prevStatus) => ({
        ...prevStatus,
        [tag.id]: true,
      }));
      await updateAssemblies.mutateAsync(updatedAssemblies);
      await deleteAssemblyTag.mutateAsync(tag.id);
      Snackbar.success(`${tag?.name} ${lang.successfullyDeleted}`);
      deleteConfirmDialog.onCloseModal();
    } catch (error: any) {
      Snackbar.error(lang.failedToDeleteAsssemblyTag);
    }
  };

  return (
    <>
      {hasData ? (
        <TableBodyContainer>
          <Table stickyHeader>
            <CustomTableHead>
              <TableRow>
                {columns.map((column) => (
                  <CustomTableCell key={column.key} isdoublewidth={column.key}>
                    <Box
                      display="flex"
                      border="none"
                      alignItems="center"
                      justifyContent="start"
                      gap={3}>
                      {column.label}
                      {column.sortIcon && (
                        <Box
                          width={14}
                          height={31}
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-around"
                          marginRight={0}>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.ASC)
                            }>
                            <KeyboardArrowUpIconStyled
                              data-testid={
                                DataTestID.ManageAssemblyTagUpArrowIcon
                              }
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.ASC &&
                                isActive
                              }
                            />
                          </ArrowIcon>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.DESC)
                            }>
                            <KeyboardArrowDownIconStyled
                              data-testid={
                                DataTestID.ManageAssemblyTagDownArrowIcon
                              }
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.DESC &&
                                isActive
                              }
                            />
                          </ArrowIcon>
                        </Box>
                      )}
                    </Box>
                  </CustomTableCell>
                ))}
                <CustomTableCell />
                <CustomTableCell />
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {assemblyTagWithAssembliesCount?.pages.map((page) =>
                page.data.map((row) => (
                  <CustomTableRow key={row.id}>
                    {columns.map((column) => (
                      <CustomTableCell
                        key={column.key}
                        isdoublewidth={column.key}>
                        {assemblyTagWithAssemblyCountData(row, column)}
                      </CustomTableCell>
                    ))}
                    <CustomTableCellRight>
                      {!isViewOnly && (
                        <IconButton
                          size="small"
                          aria-label="more"
                          onClick={() => {
                            navigate(PATHS.ASSEMBLY_TAGS_EDIT(`${row.id}`));
                          }}>
                          <EditOutlinedIcon />
                        </IconButton>
                      )}
                    </CustomTableCellRight>

                    <CustomTableCellRight>
                      {!isViewOnly && (
                        <IconButton
                          size="small"
                          aria-label="more"
                          data-testid={DataTestID.ManageAssemblyDeleteButton}
                          onClick={() => {
                            setSelectedAssemblyTag(row.id);
                            deleteConfirmDialog.onOpenModal(() =>
                              deleteTag(row),
                            );
                          }}>
                          <DeleteOutlinedIcon color="error" />
                        </IconButton>
                      )}
                    </CustomTableCellRight>
                  </CustomTableRow>
                )),
              )}
            </TableBody>
          </Table>
          {hasNextPage && (
            <InViewBoxContainer>
              <Box ref={inViewRef} />
            </InViewBoxContainer>
          )}
        </TableBodyContainer>
      ) : (
        <NoResultAlert />
      )}
      <SimpleDialog
        actionColor="error"
        open={deleteConfirmDialog.open}
        title={lang.assemblyTagDeleteModal.title}
        subTitle={lang.assemblyTagDeleteModal.subTitle}
        confirmText={lang.deleteText}
        cancelText={lang.cancel}
        onClose={deleteConfirmDialog.onCloseModal}
        onClickCancel={deleteConfirmDialog.onCloseModal}
        disabled={deletingStatus[selectedAssemblyTag]}
        onClickConfirm={deleteConfirmDialog.customFn}
      />
    </>
  );
};
